import { useContext } from 'react';

import ModalContext from '../../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../../hooks/useAnalytics';
import useModuleAccess from '../../../../../../hooks/useModuleAccess';
import { FEATURE_ACCESS } from '../../../../../../core/access/module-access';
import AppsContext from '../../../../_contexts/AppsContext';
import ModuleContext from '../../../../_contexts/ModuleContext';
import { App, LinkAppTypes, PlatformTypes } from '../../../../api/appsApi';
import ModuleHeaderTag from '../../../../components/ModuleHeaderTag';
import WordlistModal from '../../../../../standalone-wordlist/WordlistModal';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../../constants/analyticsEvents';

import PillGroup from '@pelckmans/elementis/components/pill-group';

function getDisabledTooltipKey(appsAllowed: boolean, app: App): string | undefined {
  if (app.state === 'preview') return 'PAGES.TOC_LEVEL.APPS.WORD_LIST.PREVIEW_TOOLTIP';
  if (!appsAllowed) return 'PAGES.TOC_LEVEL.APPS.WORD_LIST.NO_LICENSE_TOOLTIP';
}

export default function useWordListApps() {
  const { apps } = useContext(AppsContext);
  const { module } = useContext(ModuleContext);
  const { showModal } = useContext(ModalContext);
  const { capture } = useAnalytics();
  const { appsAccess } = useModuleAccess(module);
  const appsAllowed = appsAccess === FEATURE_ACCESS.ALLOWED;

  const wordLists = module
    ? apps.filter(
        app =>
          app.platformType === PlatformTypes.P_INTERN &&
          app.platformLinkType === LinkAppTypes.WORDLIST &&
          app.moduleId === module.id,
      )
    : [];

  const openWordlist = async (app: App) => {
    if (!module) return;

    capture({
      objectId: app.id,
      eventType: ANALYTICS_EVENT_TYPES.APP_ACCESSED,
      subLocationId: module.isExerciseModule
        ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
        : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
    });

    showModal(
      <WordlistModal
        id="wordlist-modal"
        wordlistId={app.linkedApp?.id!}
        name={app.name}
        module={module}
        headerProps={{
          title: app.name,
          titleTooltip: app.name,
          tags: (
            <PillGroup aria-label={undefined} role={undefined}>
              <ModuleHeaderTag module={module} />
            </PillGroup>
          ),
        }}
      />,
    );
  };

  return {
    wordLists,
    openWordlist,
    getDisabledTooltipKey: (app: App) => getDisabledTooltipKey(appsAllowed, app),
  };
}
